import {gql} from '@apollo/client';
import {Color} from '@benchling/nucleus-ui';
import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {
  ComplexPolymerAttachmentStyle,
  ComplexPolymerConnection,
  ComplexPolymerType,
  EntityAssociations,
  ResolvedAttachmentInput,
  resolvedAttachmentHash,
} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {ComplexPolymerAttachConjugateSection} from 'modbio/complex-polymers/tabs/ComplexPolymerAttachConjugateModal';
import {
  findResolvedAttachmentForConnection,
  getComplexPolymerConnectionFromConjugateConnection,
  getPolymersForAttachmentOnCreate,
  isCompleteConjugateConnection,
  useAttachConjugateState,
  validateConnectionSideForSubmission,
} from 'modbio/complex-polymers/tabs/ComplexPolymerAttachConjugateModalUtil';
import {
  ConjugateConnection,
  ConjugateConnectionMap,
} from 'modbio/complex-polymers/tabs/ComplexPolymerComponentsSummaryUtil';

import {getExistingResolvedAttachmentInputs} from '../ComplexPolymerUtil';
import getResolvedAttachmentsFromConjugateConnections from '../tabs/getResolvedAttachmentsFromConjugateConnections';
import {
  ComplexPolymerComponentEntitiesQuery,
  ComplexPolymerComponentEntitiesQueryVariables,
} from './__generated/ComplexPolymerComponentEntitiesQuery';
import {ConjugateSelectionPanel} from './CreateComplexPolymerConjugateSelectionPanel';
import {useCreateComplexPolymerModalContext} from './CreateComplexPolymerModalContext';
import {
  CreateComplexPolymerMoleculeGraphQL,
  CreateComplexPolymerOligoGraphQL,
  isComponentMolecule,
  isComponentOligo,
  makeConjugateConnectionsForCreate,
} from './CreateComplexPolymerUtil';

export const COMPLEX_POLYMER_COMPONENT_ENTITIES_QUERY = gql`
  query ComplexPolymerComponentEntitiesQuery($ids: [StrictString!]!)
  @engComponent(name: MODIFIED_BIOLOGICS)
  @skipAudit {
    bioentities(ids: $ids) {
      ... on SequenceNode {
        id
        bases
        nucleotideType
        modifiedNucleotides {
          sugarMonomerId
          baseMonomerId
          phosphateMonomerId
          position
        }
        length
      }
      ... on FolderItemInterface {
        id
        name
        tagSchema {
          id
          name
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const MOLECULE_ATTACHMENT_FOR_CREATE_QUERY = gql`
  query MoleculeAttachmentForCreateQuery($moleculeId: StrictString!, $polymerRef: StrictString!)
  @engComponent(name: MODIFIED_BIOLOGICS)
  @skipAudit {
    moleculeInComplexPolymer(moleculeId: $moleculeId, polymerRef: $polymerRef) {
      ...MoleculeInComplexPolymerGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('MoleculeInComplexPolymer').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const NUCLEOTIDE_ATTACHMENT_FOR_CREATE_QUERY = gql`
  query NucleotideAttachmentForCreateQuery(
    $oligoId: StrictString
    $polymerRef: StrictString!
    $residueIndex: StrictInt!
    $complexPolymerType: GQLComplexPolymerType!
  ) @engComponent(name: MODIFIED_BIOLOGICS) @skipAudit {
    nucleotideInComplexPolymer(
      oligoId: $oligoId
      polymerRef: $polymerRef
      residueIndex: $residueIndex
      complexPolymerType: $complexPolymerType
    ) {
      ...NucleotideInComplexPolymerGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('NucleotideInComplexPolymer').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface ConjugateSelectionParams {
  // Refs of all conjugates that have been selected to be part of the complex polymer
  selectedConjugateRefs: Array<string>;
  // Index in selectedConjugateRefs of the conjugate that is currently being attached
  currentConjugateRefIdx: number;
  setCurrentConjugateRefIdx: (idx: number) => void;
}

export interface CreateComplexPolymerAttachConjugatePaneProps {
  complexPolymerType: ComplexPolymerType;
  selectedEntityIds: Array<string>;
  selectedConjugateIds: Array<string>;
  conjugateSelectionParams: ConjugateSelectionParams;
  connectionInputs: Array<ComplexPolymerConnection>;
  setConnectionInputs: (connectionInputs: Array<ComplexPolymerConnection>) => void;
  attachedConjugateRefs: Array<string>;
  setAttachedConjugateRefs: (refs: Array<string>) => void;
  resolvedAttachmentInputs: Array<ResolvedAttachmentInput>;
  setResolvedAttachmentInputs: (attachments: Array<ResolvedAttachmentInput>) => void;
  entityAssociations: EntityAssociations;
}

export default function CreateComplexPolymerAttachConjugatePane({
  complexPolymerType,
  selectedEntityIds,
  selectedConjugateIds,
  conjugateSelectionParams,
  connectionInputs,
  setConnectionInputs,
  attachedConjugateRefs,
  setAttachedConjugateRefs,
  resolvedAttachmentInputs,
  setResolvedAttachmentInputs,
  entityAssociations,
}: CreateComplexPolymerAttachConjugatePaneProps): JSX.Element {
  return (
    <BenchlingQuery<ComplexPolymerComponentEntitiesQuery, ComplexPolymerComponentEntitiesQueryVariables>
      query={COMPLEX_POLYMER_COMPONENT_ENTITIES_QUERY}
      renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
      variables={{ids: selectedEntityIds}}
    >
      {({bioentities}) => {
        // For the complex polymer edit path, we do not allow creating attachments with conjugates that are inaccessible
        // To match this behavior and to simplify the logic in this flow, we filter down to only accessible conjugates
        const oligos = bioentities.filter((bioentity): bioentity is CreateComplexPolymerOligoGraphQL =>
          isComponentOligo(bioentity)
        );
        const molecules = bioentities.filter((bioentity): bioentity is CreateComplexPolymerMoleculeGraphQL =>
          isComponentMolecule(bioentity)
        );
        const conjugateOligos = oligos.filter((oligo) => selectedConjugateIds.includes(oligo.id));
        const conjugates: Array<CreateComplexPolymerMoleculeGraphQL | CreateComplexPolymerOligoGraphQL> = [
          ...molecules,
          ...conjugateOligos,
        ];
        const conjugateConnectionMap = makeConjugateConnectionsForCreate(
          conjugates,
          entityAssociations,
          connectionInputs,
          resolvedAttachmentInputs
        );

        const {selectedConjugateRefs, currentConjugateRefIdx} = conjugateSelectionParams;
        const currentConjugateRef = selectedConjugateRefs[currentConjugateRefIdx];
        return (
          <div className={css(styles.attachConjugatePane)}>
            <div className={css(styles.conjugateSelectionPanel)}>
              <ConjugateSelectionPanel
                conjugates={conjugates}
                conjugateSelectionParams={conjugateSelectionParams}
                attachedConjugateRefs={attachedConjugateRefs}
                entityAssociations={entityAssociations}
              />
            </div>
            <div className={css(styles.attachmentSection)}>
              <CreateComplexPolymerAttachConjugateSection
                key={currentConjugateRef}
                isEditMode={attachedConjugateRefs.includes(currentConjugateRef)}
                complexPolymerType={complexPolymerType}
                oligoPolymers={oligos}
                conjugateConnectionMap={conjugateConnectionMap}
                conjugateSelectionParams={conjugateSelectionParams}
                connectionInputs={connectionInputs}
                setConnectionInputs={setConnectionInputs}
                entityAssociations={entityAssociations}
                attachedConjugateRefs={attachedConjugateRefs}
                setAttachedConjugateRefs={setAttachedConjugateRefs}
                resolvedAttachmentInputs={resolvedAttachmentInputs}
                setResolvedAttachmentInputs={setResolvedAttachmentInputs}
              />
            </div>
          </div>
        );
      }}
    </BenchlingQuery>
  );
}

function addConnection(
  connectionInputs: Array<ComplexPolymerConnection>,
  setConnectionInputs: (connectionInputs: Array<ComplexPolymerConnection>) => void,
  resolvedAttachmentInputs: Array<ResolvedAttachmentInput>,
  setResolvedAttachmentInputs: (attachments: Array<ResolvedAttachmentInput>) => void,
  conjugateConnection: ConjugateConnection,
  startingConjugateRef: string,
  attachedConjugateRefs: Array<string>,
  setAttachedConjugateRefs: (refs: Array<string>) => void,
  selectedConjugateRefIdx: number,
  setSelectedConjugateRefIdx: (idx: number) => void,
  isEditMode: boolean
): void {
  assert(
    isCompleteConjugateConnection(conjugateConnection),
    'Expected conjugate connection to be connected.'
  );
  // In the case that we are editing a connection or attachment that has already been defined, remove the connection
  // that corresponds with this conjugate
  const newConnectionInputs = connectionInputs.filter(
    (connection) => connection.target.polymer_ref !== startingConjugateRef
  );

  const newConnection = getComplexPolymerConnectionFromConjugateConnection(conjugateConnection);

  setConnectionInputs([...newConnectionInputs, newConnection]);

  if (
    conjugateConnection.attachmentStyle === ComplexPolymerAttachmentStyle.DRAWN &&
    conjugateConnection.attachmentMolfile != null
  ) {
    const existingResolvedAttachment = findResolvedAttachmentForConnection(
      newConnection,
      resolvedAttachmentInputs
    );
    const existingAttachmentConnections = (existingResolvedAttachment?.connections_involved ?? []).filter(
      (connection) => connection.target.polymer_ref !== conjugateConnection.helmReference
    );

    setResolvedAttachmentInputs([
      ...resolvedAttachmentInputs.filter(
        (resolvedAttachment) =>
          existingResolvedAttachment == null ||
          resolvedAttachmentHash(resolvedAttachment) !== resolvedAttachmentHash(existingResolvedAttachment)
      ),
      {
        molfile: conjugateConnection.attachmentMolfile,
        connections_involved: [...existingAttachmentConnections, newConnection],
      },
    ]);
  }
  if (!isEditMode) {
    setAttachedConjugateRefs([...attachedConjugateRefs, startingConjugateRef]);
  }
  // Automatically move to the next conjugate on attachment
  setSelectedConjugateRefIdx(selectedConjugateRefIdx + 1);
}

export interface CreateComplexPolymerAttachConjugateSectionProps {
  isEditMode: boolean;
  complexPolymerType: ComplexPolymerType;
  oligoPolymers: Array<CreateComplexPolymerOligoGraphQL>;
  conjugateConnectionMap: ConjugateConnectionMap;
  conjugateSelectionParams: ConjugateSelectionParams;
  connectionInputs: Array<ComplexPolymerConnection>;
  setConnectionInputs: (connectionInputs: Array<ComplexPolymerConnection>) => void;
  entityAssociations: EntityAssociations;
  attachedConjugateRefs: Array<string>;
  setAttachedConjugateRefs: (ids: Array<string>) => void;
  resolvedAttachmentInputs: Array<ResolvedAttachmentInput>;
  setResolvedAttachmentInputs: (attachments: Array<ResolvedAttachmentInput>) => void;
}

export function CreateComplexPolymerAttachConjugateSection({
  isEditMode,
  complexPolymerType,
  oligoPolymers,
  conjugateConnectionMap,
  conjugateSelectionParams,
  connectionInputs,
  setConnectionInputs,
  entityAssociations,
  attachedConjugateRefs,
  setAttachedConjugateRefs,
  resolvedAttachmentInputs,
  setResolvedAttachmentInputs,
}: CreateComplexPolymerAttachConjugateSectionProps): JSX.Element {
  const {selectedConjugateRefs, currentConjugateRefIdx, setCurrentConjugateRefIdx} = conjugateSelectionParams;
  const startingConjugateRef = selectedConjugateRefs[currentConjugateRefIdx];

  const attachConjugateState = useAttachConjugateState(
    isEditMode,
    startingConjugateRef,
    conjugateConnectionMap
  );

  const {connection, attachmentMolfile} = attachConjugateState;

  const {setBannerProps} = useCreateComplexPolymerModalContext();

  function setAttachment(conjugateConnection: ConjugateConnection): void {
    validateConnectionSideForSubmission(
      connection.attachment_style ?? ComplexPolymerAttachmentStyle.LEAVING_GROUP,
      connection.source
    );
    validateConnectionSideForSubmission(
      connection.attachment_style ?? ComplexPolymerAttachmentStyle.LEAVING_GROUP,
      connection.target
    );

    const newConjugateConnection = {
      ...conjugateConnection,
      helmReference: connection.target.polymer_ref,
      residueIndex: connection.target.residue_index,
      rGroup: connection.target.r_group,
      modificationType: connection.target.modification_type,
      linkedEntityResidueIndex: connection.source.residue_index ?? undefined,
      linkedEntityHelmReference: connection.source.polymer_ref,
      linkedEntityRGroup: connection.source.r_group,
      linkedEntityModificationType: connection.source.modification_type,
      attachmentStyle: connection.attachment_style,
      attachmentMolfile,
    };

    addConnection(
      connectionInputs,
      setConnectionInputs,
      resolvedAttachmentInputs,
      setResolvedAttachmentInputs,
      newConjugateConnection,
      startingConjugateRef,
      attachedConjugateRefs,
      setAttachedConjugateRefs,
      currentConjugateRefIdx,
      setCurrentConjugateRefIdx,
      isEditMode
    );
  }

  return (
    <ComplexPolymerAttachConjugateSection
      isEditMode={isEditMode}
      complexPolymerType={complexPolymerType}
      conjugateConnectionMap={conjugateConnectionMap}
      forCreate={true}
      onClickAttach={setAttachment}
      attachConjugateState={attachConjugateState}
      resolvedAttachments={getExistingResolvedAttachmentInputs(
        getResolvedAttachmentsFromConjugateConnections(conjugateConnectionMap, resolvedAttachmentInputs)
      )}
      simplePolymers={getPolymersForAttachmentOnCreate(
        complexPolymerType,
        oligoPolymers,
        conjugateConnectionMap,
        entityAssociations
      )}
      setBannerProps={setBannerProps}
    />
  );
}

const styles = StyleSheet.create({
  attachConjugatePane: {
    minHeight: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    margin: '0px',
    flexGrow: 1,
  },
  attachmentSection: {
    width: '100%',
    display: 'flex',
    flexGrow: 1,
  },
  conjugateSelectionPanel: {
    width: '25%',
    borderRight: `1px solid ${Color.GRAY_300}`,
  },
});
